import { useElementSize } from '@mantine/hooks';
import cx from 'clsx';
import { motion } from 'motion/react';

import classes from './HudBox.module.css';
import { containerTransition } from './transitions.ts';
import {
  type Corner,
  getCornerBracket,
  getFramePath,
  getFrameStrokePath,
  getInverseClipPath,
} from '#plugin-chat/components/hud/utils.ts';

export type HudBoxProps = {
  children?: React.ReactNode;
  bevel?: number;
  cornerThickness?: number;
  cornerLength?: number;
  strokeWidth?: number;
  primaryHue?: number;
  accentHue?: number;
  showGlint?: boolean;
  className?: string;
  corners?: Corner[];
  /** Corners to render with 90-degree angles instead of beveled. These corners will not render brackets. */
  sharpCorners?: Corner[];
  glintColor?: string;
  /** Whether to show background layer. */
  background?: boolean;
  /** Enables entrance animation with height reveal and visual effects. */
  animate?: boolean;
  /** Number of orbital particles animating along the frame path. 0 = no particles. */
  particleCount?: number;
  style?: React.CSSProperties;
};

export function HudBox({
  children,
  bevel = 24,
  cornerThickness = 8,
  cornerLength = 60,
  strokeWidth = 4,
  primaryHue = 195,
  accentHue = 195,
  showGlint = true,
  className,
  corners = ['tl', 'br'],
  sharpCorners = [],
  glintColor = '#fff',
  animate = true,
  particleCount = 1,
  background = true,
  style,
}: HudBoxProps) {
  // TODO: Customizable "box" component that defaults to more plain style, but supports composing in the more complex HUD style via props or children?
  // TODO: Make composable, e.g. <HudBox><HudBox.Frame>...</HudBox.Frame><HudBox.Corner type="tl" />...</HudBox>
  const { ref, width, height } = useElementSize();
  const hasSize = width > 0 && height > 0;

  // Generate path data for clip-path
  const framePath = hasSize ? getFramePath(width, height, bevel, 0, sharpCorners) : null;
  const strokePath = hasSize ? getFrameStrokePath(width, height, bevel, strokeWidth, sharpCorners) : null;

  // Generate corner bracket paths (exclude sharp corners - they don't get brackets)
  const bracketCorners = corners.filter((corner) => !sharpCorners.includes(corner));
  const cornerPaths = hasSize
    ? bracketCorners.map((corner) => ({
        corner,
        path: getCornerBracket(width, height, bevel, cornerThickness, cornerLength, corner).path,
      }))
    : [];

  // CSS custom properties for theming
  const cssVars = {
    '--hud-clip-path': framePath ? `path('${framePath}')` : 'none',
    '--hud-clip-path-inverse': framePath
      ? `path(evenodd, '${getInverseClipPath(width, height, framePath, 20)}')`
      : 'none',
    '--hud-stroke-path': strokePath ? `path(evenodd, '${strokePath}')` : 'none',
    '--hud-frame-path': framePath ? `path('${framePath}')` : 'none',
    '--hud-primary-hue': primaryHue,
    '--hud-accent-hue': accentHue,
    '--hud-stroke-width': `${strokeWidth}px`,
    '--hud-glint-color': glintColor,
    '--hud-border-z-index': 'initial',
    ...style,
  } as React.CSSProperties;

  // Generate particles with staggered animation delays
  const particles =
    hasSize && particleCount > 0
      ? Array.from({ length: particleCount }, (_, i) => ({
          index: i,
          delay: -(8 * (i / particleCount)), // Negative delay for staggered start positions
        }))
      : [];

  return (
    <motion.div
      animate={animate ? 'show' : undefined}
      className={cx(classes.container, className)}
      initial={animate ? 'hidden' : undefined}
      // layout={layout}
      ref={ref}
      style={cssVars}
      variants={animate ? containerTransition : undefined}
    >
      {/* Background layer - clipped to frame shape */}
      {hasSize && background && (
        <>
          <div className={classes.background} />
          <div className={classes.glow} />
        </>
      )}

      {/* Border/stroke layer - uses mask to show only border area */}
      {hasSize && <div className={classes.border} />}

      {/* Orbital particles */}
      {particles.map(({ index, delay }) => (
        <div
          className={classes.particle}
          key={index}
          style={{ '--particle-delay': `${delay}s` } as React.CSSProperties}
        />
      ))}

      {/* Trail particles - one-shot animation on hover/focus */}
      {/* {hasSize && ( */}
      {/*   <> */}
      {/*     <div className={classes.trailParticle} /> */}
      {/*     <div className={classes.trailParticle} /> */}
      {/*     <div className={classes.trailParticle} /> */}
      {/*   </> */}
      {/* )} */}

      {/* Corner brackets */}
      {cornerPaths.map(({ corner, path }) => (
        <div
          className={classes.corner}
          data-corner={corner}
          key={corner}
          style={{ '--corner-clip-path': `path('${path}')` } as React.CSSProperties}
        />
      ))}

      {/* Glint effect - one-time entrance animation */}
      {hasSize && showGlint && animate && <div className={classes.glint} />}

      {/* Content */}
      <motion.div
        // animate={animate ? 'show' : undefined}
        className={classes.content}
        // initial={animate ? 'hidden' : undefined}
        layout
        // variants={animate ? containerTransition : undefined}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}
